import { cloneElement, type ReactElement, type ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import infoCircleIcon from "@/assets/icons/info-circle.svg";
import xCloseIcon from "@/assets/icons/x-close.svg";

import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { Text } from "./Text";

type NoticeVariant = "neutral" | "success" | "danger" | "warning" | "special";

const NOTICE_THEMES = {
  neutral: "bg-feedback-light text-text-on-primary",
  success: "bg-feedback-success text-text-on-success-secondary",
  danger: "bg-feedback-error text-text-on-danger-secondary",
  warning: "bg-feedback-warning text-text-on-warning-secondary",
  special: "bg-feedback-primary text-text-on-primary",
} as const;

type NoticeProps = {
  icon?: ReactElement<{ size?: number | string }>;
  "data-testid"?: string;
  variant?: NoticeVariant;
  action?: {
    label: string;
    onClick: () => void;
  };
  actions?: ReactNode;
} & (
  | {
      message: ReactNode;
      customBody?: never;
    }
  | {
      message?: never;
      customBody: React.ReactElement;
    }
);

export function Notice({
  icon,
  message,
  customBody,
  variant = "neutral",
  action,
  actions,
  "data-testid": dataTestId,
}: NoticeProps): ReactNode {
  return (
    <div
      className={twJoin(
        "flex w-full items-center gap-space-sm overflow-hidden rounded-lg p-notice",
        NOTICE_THEMES[variant],
      )}
      data-testid={dataTestId}
    >
      <div className="flex min-w-0 items-center gap-space-sm">
        {icon ? cloneElement(icon, { size: 20 }) : <Icon name={infoCircleIcon} size={20} />}
        {message != null && (
          <Text as="div" variant="caption-emphasis" tone="inherit" overflow="preserve">
            {message}
          </Text>
        )}
        {customBody}
      </div>
      {(actions || action) && (
        <div className="ml-auto flex shrink-0 items-center gap-space-md">
          {actions}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className="min-h-notice-action shrink-0 text-caption-emphasis underline"
            >
              {action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

type ClosableNoticeVariant = "special" | "warning" | "success";

const CLOSABLE_NOTICE_THEMES = {
  special: "bg-closable-notice-special text-text-on-primary",
  warning: "bg-closable-notice-warning text-text-on-warning-secondary",
  success: "bg-closable-notice-success text-text-on-success-secondary",
} as const;

type ClosableNoticeProps = {
  message: ReactNode;
  variant?: ClosableNoticeVariant;
  icon?: ReactElement<{ size?: number | string }>;
  withIcon?: boolean;
  closeLabel: string;
  onClose: () => void;
  "data-testid"?: string;
};

export function ClosableNotice({
  message,
  variant = "special",
  icon,
  withIcon = true,
  closeLabel,
  onClose,
  "data-testid": dataTestId,
}: ClosableNoticeProps): ReactNode {
  return (
    <div
      className={twJoin(
        "flex w-full items-center gap-space-md overflow-hidden rounded-lg p-space-sm",
        CLOSABLE_NOTICE_THEMES[variant],
      )}
      data-testid={dataTestId}
    >
      <div className="flex min-w-0 flex-1 items-center gap-space-xs">
        {withIcon && (icon ? cloneElement(icon, { size: 16 }) : <Icon name={infoCircleIcon} size={16} />)}
        <Text variant="caption" tone="inherit" overflow="truncate">
          {message}
        </Text>
      </div>
      <IconButton
        title={closeLabel}
        withTooltip={false}
        onClick={onClose}
        size="xs"
        variant="ghostSecondary"
        icon={<Icon name={xCloseIcon} size={16} tone={variant === "special" ? "on-primary" : variant} />}
      />
    </div>
  );
}
